import type { CSSProperties } from 'react';

/**
 * Theme Configuration TypeScript Interfaces
 *
 * These interfaces define the structure for whitelabel theming system.
 * Theme configs are loaded dynamically at runtime based on store-label.
 */

/**
 * Color configuration for a theme
 * All colors should be in hex format (#RRGGBB)
 */
export interface ThemeColors {
  // Primary color (main brand color)
  primary: string;
  primaryHover: string;
  primaryLight: string;
  primaryRgb: string; // Format: "R, G, B"

  // Secondary color (accent/supporting brand color)
  secondary: string;
  secondaryHover: string;
  secondaryLight: string;
  secondaryRgb: string;

  // Accent color (call-to-action, highlights)
  accent: string;
  accentHover: string;
  accentLight: string;
  accentRgb: string;

  // Background colors
  bg: string;
  bgSecondary: string;
  bgSoft: string;
  bgRgb: string;

  // Text colors
  textPrimary: string;
  textSecondary: string;
  textLight: string;
  textOnPrimary: string;
  textOnSecondary: string;

  // Border colors
  border: string;
  borderLight: string;
  borderDark: string;

  // Status colors
  success: string;
  warning: string;
  error: string;
  info: string;
}

/**
 * Typography configuration for a theme
 */
export interface ThemeTypography {
  // Font families
  fontFamilyPrimary: string; // Body text font
  fontFamilyHeading: string; // Heading/display font

  // Font sizes (rem units)
  fontSizeXs: string;
  fontSizeSm: string;
  fontSizeBase: string;
  fontSizeLg: string;
  fontSizeXl: string;
  fontSize2xl: string;
  fontSize3xl: string;
  fontSize4xl: string;
  fontSize5xl: string;

  // Font weights
  fontWeightLight: number;
  fontWeightNormal: number;
  fontWeightMedium: number;
  fontWeightSemibold: number;
  fontWeightBold: number;

  // Line heights
  lineHeightTight: number;
  lineHeightNormal: number;
  lineHeightRelaxed: number;
}

/**
 * Spacing configuration for a theme
 */
export interface ThemeSpacing {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  xl2: string;
  xl3: string;
  xl4: string;
}

/**
 * Layout configuration for a theme
 */
export interface ThemeLayout {
  containerMaxWidth: string;
  containerPadding: string;
  headerHeight: string;
  footerHeight: string;
}

/**
 * Borders and border radius configuration
 */
export interface ThemeBorders {
  borderWidth: string;
  borderWidthThick: string;

  radiusNone: string;
  radiusSm: string;
  radiusMd: string;
  radiusLg: string;
  radiusXl: string;
  radiusFull: string;
}

/**
 * Shadow configuration for a theme
 */
export interface ThemeShadows {
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

/**
 * Transition/animation configuration
 */
export interface ThemeTransitions {
  fast: string;
  base: string;
  slow: string;
}

/**
 * Z-index layering configuration
 */
export interface ThemeZIndex {
  dropdown: number;
  sticky: number;
  fixed: number;
  modalBackdrop: number;
  modal: number;
  popover: number;
  tooltip: number;
}

/**
 * Theme mode (light or dark)
 */
export type ThemeMode = "light" | "dark";

/**
 * Optional dark-mode override block
 * Stores declare this to opt in to dark mode. Only colors/shadows/daisyui
 * are overridable; typography, spacing, layout, borders, transitions and
 * zIndex stay shared across modes.
 */
export interface ThemeDarkModeConfig {
  colors: ThemeColors;
  shadows?: Partial<ThemeShadows>;
  daisyui?: DaisyUITheme;
}

/**
 * Complete theme configuration
 * This is the main interface for a theme.json file
 */
export interface ThemeConfig {
  name: string; // Theme name (e.g., "tayrona-nordic", "intersum-modern")
  version: string; // Theme version for cache busting

  colors: ThemeColors;
  typography: ThemeTypography;
  spacing: ThemeSpacing;
  layout: ThemeLayout;
  borders: ThemeBorders;
  shadows: ThemeShadows;
  transitions: ThemeTransitions;
  zIndex: ThemeZIndex;

  // DaisyUI theme mapping (optional, for DaisyUI components)
  daisyui?: DaisyUITheme;

  // Optional dark-mode color overrides. When present, the storefront
  // exposes a theme toggle and persists the user's choice per-origin.
  darkMode?: ThemeDarkModeConfig;
}

/**
 * DaisyUI theme configuration
 * Maps our theme to DaisyUI semantic colors
 * https://daisyui.com/docs/themes/
 */
export interface DaisyUITheme {
  primary: string;
  'primary-content': string;
  secondary: string;
  'secondary-content': string;
  accent: string;
  'accent-content': string;
  neutral: string;
  'neutral-content': string;
  'base-100': string;
  'base-200': string;
  'base-300': string;
  'base-content': string;
  info: string;
  success: string;
  warning: string;
  error: string;
}

/**
 * Logo configuration
 * Supports SVG paths, image URLs, or text-only logos
 */
export interface LogoConfig {
  type: 'svg' | 'image' | 'text' | 'hidden';

  // For SVG logos (inline SVG path)
  svgPath?: string;
  svgViewBox?: string; // e.g., "0 0 100 100"
  svgWidth?: string;
  svgHeight?: string;

  // For image logos (URL to image file)
  imageUrl?: string;
  imageAlt?: string;
  imageWidth?: string;
  imageHeight?: string;

  // Optional compact variant shown when the header shrinks on scroll
  // (when header.styling.scrollCompact === true). Falls back to the
  // primary imageUrl/width/height if not provided.
  imageUrlScrolled?: string;
  imageWidthScrolled?: string;
  imageHeightScrolled?: string;

  // For text logos
  text?: string;

  // Common properties
  className?: string; // Additional CSS classes
}

/**
 * Navigation item configuration
 */
export interface NavigationItem {
  label: string;
  path: string;
  icon?: string | null;
}

/**
 * Category menu configuration for theme-driven navigation
 */
export interface CategoryMenuConfig {
  style: 'flat' | 'dropdown' | 'mega';
  enabled: boolean;
  showImages: boolean;
  showDescriptions: boolean;
  position: 'in-nav' | 'below-nav';
  label?: string; // Trigger label for mega menu (default: "Products")
  allowedHandles?: string[]; // Filter to only show these top-level category handles
}

/**
 * Header navigation configuration
 */
export interface HeaderNavigation {
  style: 'horizontal' | 'vertical';
  items: NavigationItem[];
  categoryMenu?: CategoryMenuConfig;
  showCart?: boolean;
  cartStyle?: 'icon-minimal' | 'icon-with-count' | 'button';
  styling?: {
    textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
    fontWeight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
    spacing?: 'compact' | 'comfortable' | 'relaxed';
    letterSpacing?: string;
  };
}

/**
 * Header configuration
 */
export interface HeaderConfig {
  layout: 'logo-center-name-left' | 'name-left-menu-right' | 'standard';
  logoPosition: 'left' | 'center' | 'right';
  showBrandName: boolean;
  brandNamePosition?: 'left' | 'right';
  logo: LogoConfig;
  navigation: HeaderNavigation;
  styling?: {
    backgroundColor?: string;
    borderBottom?: boolean;
    borderStyle?: 'solid' | 'subtle' | 'none';
    padding?: 'compact' | 'comfortable' | 'generous';
    sticky?: boolean;
    blur?: boolean;
    /**
     * When true, the header shrinks as the user scrolls past 40px:
     * wordmark fades out, logo image crossfades to `logo.imageUrlScrolled`,
     * and the logo mark collapses to its scrolled dimensions. Tracks the
     * Shopify Broadcast theme pattern used by carolinesvedbom.com.
     */
    scrollCompact?: boolean;
  };
}

export interface FooterLinkConfig {
  label: string;
  path: string;
  icon?: string | null;
}

export interface FooterColumnConfig {
  title?: string;
  links?: FooterLinkConfig[];
}

export interface FooterBottomLinkConfig {
  label: string;
  path: string;
}

export interface FooterStylingConfig {
  backgroundColor?: 'soft' | 'surface' | 'dark' | 'transparent';
  textColor?: 'light' | 'secondary' | 'primary';
  borderTop?: boolean;
  padding?: 'compact' | 'comfortable' | 'generous';
}

export interface FooterConfig {
  layout?: 'one-column' | 'two-column' | 'three-column' | 'four-column';
  mobileColumns?: 1 | 2 | 3;
  columns?: FooterColumnConfig[];
  showNewsletter?: boolean;
  newsletterTitle?: string;
  newsletterSubtitle?: string;
  social?: Record<string, string>;
  bottomBar?: {
    copyright?: string;
    links?: FooterBottomLinkConfig[];
  };
  styling?: FooterStylingConfig;
}

export interface HeroCTAConfig {
  text: string;
  action: string;
  style?: 'solid' | 'outline' | 'ghost';
  target?: '_self' | '_blank';
}

export interface HeroBackgroundImageConfig {
  url: string;
  position?: string;
  size?: string;
  repeat?: string;
  opacity?: number;
  overlayColor?: string;
  overlayGradient?: string;
  overlayBlendMode?: CSSProperties['mixBlendMode'];
  blendMode?: CSSProperties['mixBlendMode'];
  filter?: string;
  scale?: number;
}

export interface HeroStylingConfig {
  textAlign?: 'left' | 'center' | 'right';
  textColor?: 'dark' | 'light';
  titleSize?: 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
  subtitleSize?: 'sm' | 'base' | 'lg' | 'xl' | '2xl';
  spacing?: 'compact' | 'comfortable' | 'generous';
  overlay?: 'none' | 'subtle-gradient' | 'bold-gradient' | 'translucent-white-card';
  contentMaxWidth?: string;
  backdropBlur?: 'none' | 'sm' | 'md' | 'lg';
  surface?: 'none' | 'translucent-dark' | 'translucent-light' | 'card' | 'translucent-white-card';
  /**
   * Background color of the hero band itself, independent of theme bg. Stores
   * can set this to a fixed color (e.g., `"#000000"` for an always-dark band)
   * or a CSS var (`"var(--color-bg)"`) to follow the theme. Default: theme bg.
   */
  surfaceColor?: string;
  /** Color used for the hero title when surfaceColor differs from theme bg. */
  titleColor?: string;
  titleWeight?: number;
  titleLetterSpacing?: string;
  /** Color used for the hero subtitle inside the hero band. */
  subtitleColor?: string;
  subtitleWeight?: number;
  subtitleLineHeight?: number;
  cardPadding?: string;
  cardBorderRadius?: string;
}

/**
 * Foreground mark/emblem image used by the "mark-centerpiece" hero variant.
 * Store-neutral — any store can declare this (lion, monogram, wordmark, etc).
 */
export interface HeroMarkImageConfig {
  url: string;
  alt?: string;
  widthDesktop?: string;
  widthTablet?: string;
  widthMobile?: string;
}

export interface BrandingHeroConfig {
  enabled?: boolean; // Whether to show the hero section (defaults to true)
  variant?: string;
  title: string;
  subtitle: string;
  ctaText?: string;
  ctaPrimary?: HeroCTAConfig;
  ctaSecondary?: HeroCTAConfig;
  backgroundPattern?: string;
  /**
   * Optional path to a tiling background-pattern asset (e.g. a subtle SVG
   * grid) layered behind the hero content (issue #143). The store ships the
   * asset under its store-label `assets/` dir; the Dockerfile copies that
   * tree to the build root, so the value is a root-absolute path like
   * "/grid-subtle.svg". Any store may opt in; omitting it leaves the hero
   * pattern-free.
   */
  backgroundPatternAsset?: string;
  backgroundImage?: HeroBackgroundImageConfig | null;
  backgroundGradient?: string | null;
  /**
   * Optional foreground mark image used by the "mark-centerpiece" hero variant.
   * Rendered as an `<img>`, not a CSS background, so it stays sharp and
   * accessible with a real alt text. Each store declares its own mark.
   */
  markImage?: HeroMarkImageConfig | null;
  styling?: HeroStylingConfig;
  /**
   * Optional parallax config — applies a small scroll-driven translate to
   * the markImage for editorial depth. Automatically disabled when the
   * user prefers reduced motion.
   */
  parallax?: {
    enabled?: boolean;
    offsetRatio?: number; // 0.1 = 10% of scroll distance, default 0.15
  };
}

export interface BrandingStoreInfo {
  name: string;
  tagline: string;
  description: string;
}

/**
 * About page story configuration
 */
export interface AboutStorySection {
  heading: string;
  content: string;
}

export interface AboutConfig {
  story?: {
    title?: string;
    sections?: AboutStorySection[];
    tagline?: string;
  };
}

/**
 * Benefit / value proposition (used in values grid)
 */
export interface ValueItemConfig {
  title: string;
  description: string;
  icon?: string; // Heroicons name (e.g., "truck", "shield-check")
}

/**
 * Trust signal badges (artisan credentials, certifications, etc.)
 */
export interface TrustSignalConfig {
  title: string;
  description?: string;
  icon?: string;
}

/**
 * Enterprise/B2B-specific content (industries, client logos, credentials)
 * — opt-in per store via the `enterprise` branding block.
 */
export interface EnterpriseIndustryConfig {
  label: string;
  handle: string;
  description?: string;
}

export interface EnterpriseClientLogoConfig {
  name: string;
  imageUrl?: string;
}

export interface EnterpriseCredentialConfig {
  title: string;
  description?: string;
  icon?: string;
}

export interface EnterpriseCredentialsBandConfig {
  /**
   * When true, the credentials bar renders as a full-bleed band that
   * escapes the page's max-width container and spans the full viewport
   * width (issue #143). Stores omit it to keep the bar constrained.
   */
  fullBleed?: boolean;
}

export interface EnterpriseConfig {
  industries?: EnterpriseIndustryConfig[];
  clientLogos?: EnterpriseClientLogoConfig[];
  credentials?: EnterpriseCredentialConfig[];
  credentialsBand?: EnterpriseCredentialsBandConfig;
}

/**
 * Product listing page configuration
 */
export interface ProductListingConfig {
  layout?: 'grid' | 'list';
  columns?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
    wide?: number;
  };
  filters?: {
    position?: 'sidebar' | 'top';
    style?: 'minimal' | 'pills' | 'detailed';
    showSearch?: boolean;
    showSort?: boolean;
  };
  pagination?: {
    type?: 'load-more' | 'numbered' | 'infinite';
    itemsPerPage?: number;
  };
  itemNoun?: string; // "product" | "service" | etc. — affects copy
  searchPlaceholder?: string;
  styling?: {
    gap?: 'tight' | 'comfortable' | 'generous';
    cardStyle?: string;
    hoverEffect?: string;
  };
}

/**
 * Product card configuration
 */
export interface ProductCardConfig {
  variant?: 'image-dominant-minimal' | 'info-focused' | string;
  imageAspectRatio?: 'portrait' | 'square' | '16:9' | 'landscape';
  showQuickView?: boolean;
  showWishlist?: boolean;
  showAddToCart?: boolean;
  priceDisplay?: 'prominent' | 'prominent-with-term' | 'subtle';
  badgePosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  styling?: {
    borderRadius?: string;
    border?: string;
    shadow?: string;
    hoverShadow?: string;
    hoverBorder?: string;
    padding?: string;
    transition?: string;
    backgroundColor?: string;
    imageHoverScale?: number;
    hoverTranslateY?: string;
  };
  typography?: {
    titleSize?: string;
    titleWeight?: string | number;
    titleColor?: string;
    titleLetterSpacing?: string;
    priceSize?: string;
    priceWeight?: string | number;
    priceColor?: string;
    descriptionSize?: string;
    descriptionColor?: string;
  };
  spacing?: {
    gap?: string;
    gridGap?: string;
    contentPadding?: string;
  };
  salePill?: {
    enabled?: boolean;
    backgroundColor?: string;
    color?: string;
    text?: string; // Defaults to "Sale"
    borderRadius?: string;
  };
  imageCursor?: 'default' | 'zoom-in' | 'pointer';
}

/**
 * Product detail page configuration
 */
export interface ProductDetailConfig {
  layout?: 'two-column-equal' | 'sidebar-right' | 'sidebar-left' | 'stacked';
  imageGallery?: {
    variant?: 'thumbnails-left' | 'thumbnails-bottom' | 'single-large' | 'grid';
    zoom?: boolean;
    fullscreen?: boolean;
    cursor?: 'default' | 'zoom-in' | 'pointer';
  };
  infoLayout?: 'stacked' | 'tabs' | 'accordion';
  showBreadcrumbs?: boolean;
  showSocialShare?: boolean;
  relatedProducts?: {
    show?: boolean;
    title?: string;
    count?: number;
  };
  styling?: {
    spacing?: 'compact' | 'comfortable' | 'generous';
    dividers?: 'none' | 'subtle' | 'solid';
  };
}

/**
 * Cart configuration
 */
export interface CartConfig {
  variant?: 'drawer' | 'page' | 'modal';
  drawerPosition?: 'left' | 'right' | null;
  showMiniCart?: boolean;
  showRecommendations?: boolean;
  emptyState?: {
    message?: string;
    cta?: string;
    icon?: string;
  };
  styling?: {
    itemSpacing?: 'compact' | 'comfortable' | 'generous';
    showThumbnails?: boolean;
    thumbnailSize?: 'sm' | 'md' | 'lg' | null;
  };
  /**
   * Icon name from the shared SVG registry used as a placeholder when a
   * cart item has no thumbnail, and on the empty-cart state. Keeps the
   * drawer whitelabelled without components branching on store.
   * Defaults to `"shopping-bag"`.
   */
  itemPlaceholderIcon?: string;
}

/**
 * Checkout configuration
 */
export interface CheckoutConfig {
  layout?: 'single-page' | 'multi-step';
  steps?: string[];
  showOrderSummary?: 'sticky-sidebar' | 'collapsible' | 'inline';
  trustBadges?: {
    show?: boolean;
    items?: string[];
  };
  styling?: {
    formStyle?: 'clean' | 'professional' | 'minimal';
    progressIndicator?: 'steps' | 'progress-bar' | 'none';
    spacing?: 'compact' | 'comfortable' | 'generous';
  };
  /**
   * Per-step icon name, looked up in `checkout/icons.tsx` registry
   * (`"package" | "credit-card" | "shield-check" | "user" | …`).
   * Keys match step keys used in `steps[]`.
   * Omitted keys fall back to a per-step default.
   */
  stepIcons?: Record<string, string>;
  /** Optional override labels per step key (e.g. `{ shipping: "Delivery" }`). */
  stepLabels?: Record<string, string>;
  /** Icon shown on the OrderConfirmation success screen. Default `"check-circle"`. */
  confirmationIcon?: string;
}

/**
 * Branding configuration
 * Contains all brand-specific content (not design tokens)
 */
export interface BrandingConfig {
  store?: BrandingStoreInfo; // Structured store information
  name?: string; // Backwards compatibility: flat name field
  tagline?: string; // Backwards compatibility: flat tagline field
  description?: string; // Backwards compatibility: flat description field

  // Logo configuration (deprecated - use header.logo instead)
  logo?: LogoConfig;

  // Header configuration
  header?: HeaderConfig;

  // Hero section content (for home page)
  hero: BrandingHeroConfig;

  // About page content
  about?: AboutConfig;

  // Benefit / value propositions grid
  values?: ValueItemConfig[];

  // Trust signals (badges, certifications)
  trustSignals?: TrustSignalConfig[];

  // Enterprise/B2B-specific content
  enterprise?: EnterpriseConfig;

  // Product listing page configuration
  productListing?: ProductListingConfig;

  // Product card configuration
  productCard?: ProductCardConfig;

  // Product detail page configuration
  productDetail?: ProductDetailConfig;

  // Cart configuration
  cart?: CartConfig;

  // Checkout configuration
  checkout?: CheckoutConfig;

  // Footer configuration (optional)
  footer?: FooterConfig;

  // Section title decoration (opt-in per store-label)
  sectionTitles?: {
    divider?: string;
  };

  // SEO metadata
  meta?: {
    title: string;
    description: string;
    keywords?: string[];
    ogImage?: string;
    favicon?: string;
    canonicalUrl?: string;
  };

  // Social links (optional)
  social?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
  };

  // Contact information (optional)
  contact?: {
    email?: string;
    phone?: string;
    address?: string | {
      street?: string;
      city?: string;
      postalCode?: string;
      country?: string;
      region?: string;
    };
  };
}

/**
 * Combined theme and branding context
 * This is what the React context will provide
 */
export interface ThemeContextValue {
  theme: ThemeConfig | null;
  branding: BrandingConfig | null;
  isLoading: boolean;
  error: Error | null;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  supportsDarkMode: boolean;
}

/**
 * Default theme values (fallback)
 * Used when theme fails to load or during development
 */
export const DEFAULT_THEME: ThemeConfig = {
  name: 'default',
  version: '1.0.0',
  colors: {
    primary: '#212121',
    primaryHover: '#000000',
    primaryLight: '#424242',
    primaryRgb: '33, 33, 33',
    secondary: '#D4745C',
    secondaryHover: '#C46350',
    secondaryLight: '#E89580',
    secondaryRgb: '212, 116, 92',
    accent: '#4A90A4',
    accentHover: '#3D7A8A',
    accentLight: '#6BADC1',
    accentRgb: '74, 144, 164',
    bg: '#FFFFFF',
    bgSecondary: '#F7F9FA',
    bgSoft: '#F2F2F2',
    bgRgb: '255, 255, 255',
    textPrimary: '#212121',
    textSecondary: '#646464',
    textLight: '#757575',
    textOnPrimary: '#FFFFFF',
    textOnSecondary: '#FFFFFF',
    border: '#F0F0F0',
    borderLight: '#F6F6F6',
    borderDark: '#BDBDBD',
    success: '#15803D',
    warning: '#B45309',
    error: '#DC2626',
    info: '#2563EB',
  },
  typography: {
    fontFamilyPrimary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontFamilyHeading: "'Playfair Display', Georgia, serif",
    fontSizeXs: '0.75rem',
    fontSizeSm: '0.875rem',
    fontSizeBase: '1rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '1.875rem',
    fontSize4xl: '2.25rem',
    fontSize5xl: '3rem',
    fontWeightLight: 300,
    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightSemibold: 600,
    fontWeightBold: 700,
    lineHeightTight: 1.25,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.75,
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xl2: '3rem',
    xl3: '4rem',
    xl4: '6rem',
  },
  layout: {
    containerMaxWidth: '1280px',
    containerPadding: '1.5rem',
    headerHeight: '80px',
    footerHeight: 'auto',
  },
  borders: {
    borderWidth: '1px',
    borderWidthThick: '2px',
    radiusNone: '0',
    radiusSm: '0.125rem',
    radiusMd: '0.25rem',
    radiusLg: '0.5rem',
    radiusXl: '1rem',
    radiusFull: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  transitions: {
    fast: '150ms ease',
    base: '200ms ease',
    slow: '300ms ease',
  },
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
};

/**
 * Default branding values (fallback)
 */
export const DEFAULT_BRANDING: BrandingConfig = {
  store: {
    name: 'Intersum Store',
    tagline: 'Premium E-commerce',
    description: 'Your trusted e-commerce partner',
  },
  name: 'Intersum Store',
  tagline: 'Premium E-commerce',
  description: 'Your trusted e-commerce partner',
  logo: {
    type: 'text',
    text: 'Intersum',
  },
  hero: {
    variant: 'classic',
    title: 'Welcome to Our Store',
    subtitle: 'Discover amazing products',
    ctaText: 'Shop Now',
    ctaPrimary: {
      text: 'Shop Now',
      action: '/products',
      style: 'solid',
    },
    ctaSecondary: {
      text: 'Our Story',
      action: '/about',
      style: 'outline',
    },
    backgroundPattern: undefined,
    backgroundImage: null,
    styling: {
      textAlign: 'center',
      textColor: 'dark',
      titleSize: '5xl',
      subtitleSize: 'lg',
      spacing: 'generous',
      overlay: 'none',
      contentMaxWidth: '800px',
      backdropBlur: 'none',
      surface: 'none',
    },
  },
};
